"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var redux_persist_1 = require("redux-persist");
var getStoredState_1 = __importDefault(require("redux-persist/lib/getStoredState"));
var purgeStoredState_1 = __importDefault(require("redux-persist/lib/purgeStoredState"));
var storage_1 = __importDefault(require("redux-persist/lib/storage"));
var constants_1 = require("./constants");
var createPersistoid_1 = __importDefault(require("./createPersistoid"));
var DEFAULT_TIMEOUT = 5000;
/*
  @TODO add validation / handling for:
  - persisting a reducer which has nested _persist
  - handling actions that fire before reydrate is called
*/
var settledReducers = [];
function persistReducer(config, baseReducer) {
    if (process.env.NODE_ENV !== 'production') {
        if (!config)
            throw new Error('config is required for persistReducer');
        if (!config.key)
            throw new Error('key is required in persistor config');
        if (typeof config.stateReconciler === undefined)
            throw new Error('stateReconciler is required in persistor config for: ' +
                config.key);
    }
    var stateReconciler = config.stateReconciler, _a = config.version, version = _a === void 0 ? redux_persist_1.DEFAULT_VERSION : _a, _b = config.getStoredState, getStoredState = _b === void 0 ? getStoredState_1.default : _b, _c = config.timeout, timeout = _c === void 0 ? DEFAULT_TIMEOUT : _c;
    if (!config.storage) {
        config.storage = storage_1.default;
    }
    var _persistoid = null;
    var _purge = false;
    var _paused = true;
    var conditionalUpdate = function (state) {
        // update the persistoid only if we are rehydrated and not paused
        if (state._persist.rehydrated && _persistoid && !_paused) {
            _persistoid.update(state);
        }
        return state;
    };
    var persistedReducer = function (state, action) {
        var _persist = (state || {})._persist;
        // if (state) {
        //     delete state['_persist'];
        // }
        var updateState = function (p) {
            var _nextState = baseReducer(state, action);
            _nextState['_persist'] = p;
            return _nextState;
        };
        if (isPersistorAction(action)) {
            switch (action.type) {
                case redux_persist_1.PERSIST: {
                    var _sealed_1 = false;
                    var _timeoutId = null;
                    var _rehydrate_1 = function (payload, err) {
                        // dev warning if we are already sealed
                        if (process.env.NODE_ENV !== 'production' && _sealed_1)
                            console.error("redux-persist: rehydrate for \"" + config.key + "\" called after timeout.", payload, err);
                        // only rehydrate if we are not already sealed
                        if (!_sealed_1) {
                            action.rehydrate(config.key, payload, err);
                            _sealed_1 = true;
                        }
                    };
                    if (timeout) {
                        _timeoutId = setTimeout(function () {
                            if (_sealed_1)
                                return;
                            _rehydrate_1(undefined, new Error("redux-persist: persist timed out for persist key \"" + config.key + "\""));
                        }, timeout);
                    }
                    // @NOTE PERSIST resumes if paused.
                    _paused = false;
                    // @NOTE only ever create persistoid once, ensure we call it at least once, even if _persist has already been set
                    if (!_persistoid)
                        _persistoid = createPersistoid_1.default(config);
                    // @NOTE PERSIST can be called multiple times, noop after the first
                    if (_persist || settledReducers.includes(config.key)) {
                        if (_timeoutId) {
                            clearTimeout(_timeoutId);
                        }
                        // We still need to call the base reducer because there might be nested
                        // uses of persistReducer which need to be aware of the PERSIST action
                        return updateState(_persist);
                    }
                    if (typeof action.rehydrate !== 'function' ||
                        typeof action.register !== 'function')
                        throw new Error('redux-persist: either rehydrate or register is not a function on the PERSIST action. This can happen if the action is being replayed. This is an unexplored use case, please open an issue and we will figure out a resolution.');
                    action.register(config.key);
                    getStoredState(config).then(function (restoredState) {
                        var migrate = config.migrate ||
                            (function (s, v) { return Promise.resolve(s); });
                        migrate(restoredState, version).then(function (migratedState) {
                            _rehydrate_1(migratedState, undefined);
                        }, function (migrateErr) {
                            if (process.env.NODE_ENV !== 'production' &&
                                migrateErr)
                                console.error('redux-persist: migration error', migrateErr);
                            _rehydrate_1(undefined, migrateErr);
                        });
                    }, function (err) {
                        _rehydrate_1(undefined, err);
                    });
                    settledReducers.push(config.key);
                    persistedReducer._isPersisted = true;
                    return updateState({ version: version, rehydrated: false });
                }
                case redux_persist_1.PURGE: {
                    _purge = true;
                    action.result(purgeStoredState_1.default(config));
                    return updateState(_persist);
                }
                case redux_persist_1.FLUSH: {
                    action.result(_persistoid && _persistoid.flush());
                    return updateState(_persist);
                }
                case redux_persist_1.PAUSE: {
                    _paused = true;
                    break;
                }
                case redux_persist_1.REHYDRATE: {
                    // noop on restState if purging
                    if (_purge) {
                        return updateState(__assign(__assign({}, _persist), { rehydrated: true }));
                    }
                    // @NOTE if key does not match, will continue to default else below
                    if (action.key !== config.key) {
                        break;
                    }
                    var reducedState = baseReducer(state, action);
                    var inboundState = action.payload;
                    var reconciledRest = reducedState;
                    if (
                    // only reconcile state if stateReconciler and inboundState are both defined
                    stateReconciler !== false &&
                        inboundState != null &&
                        // only reconcile non-expired state
                        // TODO: clean storage
                        (!inboundState[constants_1.EXPIRE_KEY] ||
                            Date.now() <
                                inboundState[constants_1.EXPIRE_KEY] + config.expireAfter)) {
                        reconciledRest = stateReconciler(inboundState, state, reducedState, config);
                    }
                    reconciledRest['_persist'] = __assign(__assign({}, _persist), { rehydrated: true });
                    return conditionalUpdate(reconciledRest);
                }
                default:
                    break;
            }
        }
        // if we have not already handled PERSIST, straight passthrough
        if (!_persist)
            return baseReducer(state, action);
        // run base reducer:
        // is state modified ? return original : return updated
        var newState = baseReducer(state, action);
        if (newState === state)
            return state;
        newState['_persist'] = _persist;
        return conditionalUpdate(newState);
    };
    persistedReducer._isPersisted = false;
    return persistedReducer;
}
exports.default = persistReducer;
function isPersistorAction(action) {
    return [redux_persist_1.PERSIST, redux_persist_1.PURGE, redux_persist_1.FLUSH, redux_persist_1.PAUSE, redux_persist_1.REHYDRATE].includes(action.type);
}
//# sourceMappingURL=persistReducer.js.map